import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { useT } from "../i18n";
import { Link } from "../router";
import { A11yPanel } from "./A11yPanel";
import { Icon } from "./Icon";
import { Button } from "./ui";

/**
 * The mark: four bars of a transmission, the live one in signal amber. It is the same
 * shape as the `.sig` motif used across the site, the app and the report, and it stays readable
 * down to a 16 px favicon.
 */
export function BrandGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.5" y="13" width="3" height="8" rx="1.5" fill="currentColor" opacity="0.55" />
      <rect x="8" y="8.5" width="3" height="12.5" rx="1.5" fill="currentColor" opacity="0.8" />
      <rect x="13.5" y="3" width="3" height="18" rx="1.5" fill="#fb923c" />
      <rect x="19" y="10.5" width="3" height="10.5" rx="1.5" fill="currentColor" opacity="0.8" />
    </svg>
  );
}

export function Brand() {
  const t = useT();
  return (
    <Link href="/" class="brand" aria-label={t("app.name")}>
      <span class="mark" aria-hidden="true">
        <BrandGlyph />
      </span>
      <span>{t("app.name")}</span>
    </Link>
  );
}

export function A11yButton({ size = "sm" }: { size?: "sm" | "md" }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="secondary"
        size={size}
        icon="type"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <span class="visually-hidden">{t("nav.settings")}</span>
        <span aria-hidden="true">Aa</span>
      </Button>
      {open ? <A11yPanel onClose={() => setOpen(false)} /> : null}
    </>
  );
}

export function Header({ right }: { right?: ComponentChildren }) {
  return (
    <header class="site-header">
      <Brand />
      <nav class="row" aria-label="Primary">
        {right}
        <A11yButton />
      </nav>
    </header>
  );
}

export function Page({ children, wide }: { children: ComponentChildren; wide?: boolean }) {
  const t = useT();
  return (
    <>
      <a class="skip-link" href="#main">
        {t("app.skipToContent")}
      </a>
      <div class={wide ? "container wide" : "container"}>{children}</div>
    </>
  );
}

export function ConnectionBanner({ status }: { status: string }) {
  const t = useT();
  if (status === "open") return null;
  const text =
    status === "connecting"
      ? t("status.connecting")
      : status === "reconnecting"
        ? t("status.reconnecting")
        : null;
  if (!text) return null;
  return (
    <div class="card signal tight row" role="alert" style={{ position: "sticky", top: 8, zIndex: 30 }}>
      <Icon name={status === "reconnecting" ? "wifi-off" : "info"} />
      <span>{text}</span>
    </div>
  );
}
